import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  ArrowRight,
  Banknote,
  BookOpen,
  Building2,
  Calendar,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  FileText,
  GraduationCap,
  LogOut,
  Plus,
  RefreshCw,
  Search,
  Shield,
  Users
} from 'lucide-react';
import { api } from '../services/api';
import ConfirmDialog from './ConfirmDialog';
import { useFeatures } from '../contexts/FeaturesContext';

const formatNumber = (value) => new Intl.NumberFormat('fr-FR').format(Number(value || 0));
const formatMoney = (value) => `${formatNumber(Math.round(Number(value || 0)))} FCFA`;

const decisionLabels = {
  reconduit: 'Reconduit',
  a_revoir: 'A revoir',
  non_reconduit: 'Non reconduit'
};

const decisionClasses = {
  reconduit: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  a_revoir: 'border-amber-200 bg-amber-50 text-amber-700',
  non_reconduit: 'border-red-200 bg-red-50 text-red-700'
};

const Badge = ({ children, className = '' }) => (
  <span className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-semibold ${className}`}>
    {children}
  </span>
);

const Stepper = ({ activeStep, setActiveStep, steps }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
    <div className="flex items-start justify-between gap-2">
      {steps.map((step, index) => {
        const Icon = step.icon;
        const isActive = step.id === activeStep;
        const isDone = step.done;

        return (
          <React.Fragment key={step.id}>
            <button
              type="button"
              onClick={() => setActiveStep(step.id)}
              className="flex min-w-[112px] flex-1 flex-col items-center text-center"
            >
              <span className={`flex h-11 w-11 items-center justify-center rounded-full border-2 ${
                isActive
                  ? 'border-[#246990] bg-[#246990] text-white'
                  : isDone
                    ? 'border-emerald-600 bg-emerald-600 text-white'
                    : 'border-gray-200 bg-gray-50 text-gray-500'
              }`}>
                {isDone ? <CheckCircle size={20} /> : <Icon size={19} />}
              </span>
              <span className={`mt-3 text-sm font-bold ${isActive ? 'text-[#246990]' : 'text-gray-700'}`}>
                {step.title}
              </span>
              <span className="mt-1 text-xs text-gray-500">{step.subtitle}</span>
            </button>
            {index < steps.length - 1 && (
              <div className={`mt-5 h-0.5 flex-[0.35] rounded-full ${isDone ? 'bg-emerald-500' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  </div>
);

const InfoLine = ({ label, value, tone = 'blue' }) => {
  const colors = {
    blue: 'bg-[#246990]/5 text-[#246990] border-[#246990]/15',
    teal: 'bg-[#319190]/5 text-[#319190] border-[#319190]/15',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200'
  };

  return (
    <div className={`rounded-lg border px-4 py-3 ${colors[tone] || colors.blue}`}>
      <p className="text-[11px] font-bold uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-xl font-bold text-gray-900">{value}</p>
    </div>
  );
};

const ActionButton = ({ children, disabled, icon: Icon, loading, onClick, tone = 'primary', type = 'button' }) => {
  const classes = {
    primary: 'bg-[#246990] text-white hover:bg-[#1f5c7f] disabled:bg-gray-200 disabled:text-gray-500',
    light: 'border border-gray-200 bg-white text-[#246990] hover:bg-[#246990]/5 disabled:text-gray-400',
    danger: 'bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-200 disabled:text-gray-500'
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold shadow-sm transition-colors disabled:cursor-not-allowed ${classes[tone]}`}
    >
      {loading ? <RefreshCw size={16} className="animate-spin" /> : Icon ? <Icon size={16} /> : null}
      {children}
    </button>
  );
};

const AlertBox = ({ type = 'info', title, children }) => {
  const classes = {
    info: 'border-[#246990]/20 bg-[#246990]/5 text-[#246990]',
    success: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    warning: 'border-amber-200 bg-amber-50 text-amber-800',
    danger: 'border-red-200 bg-red-50 text-red-800'
  };

  return (
    <div className={`rounded-lg border p-4 ${classes[type] || classes.info}`}>
      {title && <p className="font-bold">{title}</p>}
      {children && <div className="mt-1 text-sm">{children}</div>}
    </div>
  );
};

const StepLayout = ({ title, subtitle, icon: Icon, children, footer }) => (
  <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
    <div className="border-b border-gray-100 px-6 py-5">
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#246990]/10 text-[#246990]">
          <Icon size={21} />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
        </div>
      </div>
    </div>
    <div className="px-6 py-6">{children}</div>
    {footer && <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-6 py-4">{footer}</div>}
  </div>
);

const MONTH_NAMES = [
  'Janvier',
  'Fevrier',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Aout',
  'Septembre',
  'Octobre',
  'Novembre',
  'Decembre'
];

const toDateInputValue = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const addDaysToDateInput = (dateInput, days) => {
  const date = dateInput ? new Date(`${dateInput}T00:00:00`) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  date.setDate(date.getDate() + days);
  return toDateInputValue(date);
};

const buildNextCalendarMonth = (months, nextYearName) => {
  const maxOrder = months.reduce((max, month) => Math.max(max, Number(month.ordre || 0)), 0);
  const sortedMonths = [...months]
    .filter((month) => month.date_fin)
    .sort((a, b) => Number(a.ordre || 0) - Number(b.ordre || 0));
  const lastMonth = sortedMonths[sortedMonths.length - 1];
  const ordre = maxOrder + 1;
  const dateDebut = addDaysToDateInput(lastMonth?.date_fin, 1);
  const debut = dateDebut ? new Date(`${dateDebut}T00:00:00`) : new Date();
  const fin = new Date(debut);
  fin.setMonth(fin.getMonth() + 1);
  fin.setDate(fin.getDate() - 1);

  return {
    id: null,
    nom: MONTH_NAMES[debut.getMonth()] || `Mois ${ordre}`,
    trimestre: Math.min(3, Math.max(1, Math.ceil(ordre / 3))),
    ordre,
    annee_scolaire: nextYearName,
    date_debut: toDateInputValue(debut),
    date_fin: toDateInputValue(fin)
  };
};

const getTenantSnapshot = () => {
  let user = {};
  try {
    user = JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    user = {};
  }

  const hostname = window.location.hostname;
  const tenant = hostname === 'localhost' || hostname === '127.0.0.1'
    ? 'www'
    : hostname.split('.')[0] || 'www';

  return {
    tenant,
    schoolName: user.nom_ecole || user.school_name || user.ecole || user.etablissement || tenant
  };
};

const YearTransitionOverlay = ({ currentYearName, nextYearName, tenantLabel }) => (
  <div className="fixed inset-0 z-[10020] flex items-center justify-center bg-slate-950/75 px-6 backdrop-blur-sm">
    <div className="w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl">
      <div className="bg-gradient-to-r from-[#246990] to-[#319190] px-8 py-7 text-white">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/75">Cloture annuelle</p>
        <h2 className="mt-2 text-2xl font-bold">Passage vers la nouvelle annee</h2>
        <p className="mt-2 text-sm text-white/85">
          Classis ferme {currentYearName}, active {nextYearName}, puis vous ramene a la connexion.
        </p>
      </div>

      <div className="px-8 py-8">
        <div className="mb-6 rounded-xl border border-[#246990]/15 bg-[#246990]/5 px-4 py-3 text-sm font-semibold text-[#246990]">
          Tenant concerne : {tenantLabel}
        </div>

        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-5">
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
            <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Annee cloturee</p>
            <p className="mt-2 text-2xl font-bold text-gray-900">{currentYearName}</p>
          </div>

          <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-[#246990] text-white shadow-lg">
            <ArrowRight size={24} />
            <span className="absolute inset-0 rounded-full border-4 border-[#319190]/50 animate-ping" />
          </div>

          <div className="rounded-xl border border-[#319190]/20 bg-[#319190]/5 p-5">
            <p className="text-xs font-bold uppercase tracking-wide text-[#319190]">Nouvelle annee active</p>
            <p className="mt-2 text-2xl font-bold text-gray-900">{nextYearName}</p>
          </div>
        </div>

        <div className="mt-7 space-y-3">
          {[
            'Verrouillage de l annee actuelle',
            'Activation de la nouvelle annee',
            'Preparation de la reconnexion'
          ].map((item, index) => (
            <div key={item} className="flex items-center gap-3 rounded-lg border border-gray-100 bg-white px-4 py-3 shadow-sm">
              <span className={`flex h-8 w-8 items-center justify-center rounded-full ${
                index === 2 ? 'bg-[#246990]/10 text-[#246990]' : 'bg-emerald-50 text-emerald-700'
              }`}>
                {index === 2 ? <RefreshCw size={16} className="animate-spin" /> : <CheckCircle size={16} />}
              </span>
              <span className="text-sm font-semibold text-gray-700">{item}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const ClotureAnnee = () => {
  // La clôture est au socle : toute école doit pouvoir changer d'année sans
  // rien demander à l'éditeur. Seule l'étape de recouvrement est vendue — elle
  // relève de la comptabilité, qui porte le recouvrement et les relances.
  const { estActive } = useFeatures();
  const recouvrementOuvert = estActive('module.comptabilite');

  const [diagnostic, setDiagnostic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [activeStep, setActiveStep] = useState('annee');

  const [creatingNextYear, setCreatingNextYear] = useState(false);
  const [preparingPedagogy, setPreparingPedagogy] = useState(false);
  const [preparingPersonnel, setPreparingPersonnel] = useState(false);
  const [personnelLoading, setPersonnelLoading] = useState(false);
  const [personnelSaving, setPersonnelSaving] = useState(false);
  const [personnelData, setPersonnelData] = useState(null);
  const [personnelSearch, setPersonnelSearch] = useState('');

  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarSaving, setCalendarSaving] = useState(false);
  const [calendarData, setCalendarData] = useState(null);
  const [calendarMonths, setCalendarMonths] = useState([]);
  const [recouvrementLoading, setRecouvrementLoading] = useState(false);
  const [recouvrementData, setRecouvrementData] = useState(null);
  const [recouvrementConfirm, setRecouvrementConfirm] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [finalConfirm, setFinalConfirm] = useState(false);
  const [showFinalizeConfirm, setShowFinalizeConfirm] = useState(false);
  const [showYearTransition, setShowYearTransition] = useState(false);

  const loadDiagnostic = async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const response = await api.anneesScolaires.getDiagnosticCloture();
      if (!response?.success) {
        throw new Error(response?.message || 'Impossible de charger la cloture annuelle');
      }
      setDiagnostic(response.data);
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadDiagnostic();
  }, []);

  const synthese = diagnostic?.synthese || {};
  const contexte = diagnostic?.contexte || {};
  const indicateurs = synthese.indicateurs || {};
  const nextYearExists = Boolean(contexte.prochaine_annee?.existe);
  const nextYearName = contexte.prochaine_annee?.nom_attendu || 'la prochaine annee';
  const currentYearName = contexte.annee_active?.nom || 'annee actuelle';

  const classesSourceCount = Number(indicateurs.classes || 0);
  const classesPreparedCount = Number(indicateurs.classes_prochaine_annee || 0);
  const employeesSourceCount = Number(indicateurs.employes_actifs ?? indicateurs.employes ?? 0);
  const employeesPreparedCount = Number(indicateurs.employes_reconduction_prepares || 0);
  const rhDiagnosticSummary = diagnostic?.domaines?.ressources_humaines?.employes_reconduction || {};
  const monthsSourceCount = Number(indicateurs.mois_scolaires || 0);
  const monthsPreparedCount = Number(indicateurs.mois_scolaires_prochaine_annee || 0);
  const recouvrementSummary = recouvrementData?.synthese || {};
  const resteARecouvrer = Number(recouvrementSummary.reste_a_recouvrer || 0);
  // Sans comptabilité l'étape n'existe pas : la tenir pour revue est ce qui
  // permet encore de clôturer. Sans cela, une école Essentiel resterait
  // bloquée sur une condition qu'aucun écran ne lui donne les moyens de lever.
  const recouvrementReviewed = !recouvrementOuvert
    || (Boolean(recouvrementData) && (resteARecouvrer <= 0 || recouvrementConfirm));

  const classesReady = nextYearExists && classesSourceCount > 0 && classesPreparedCount >= classesSourceCount;
  const personnelReady = nextYearExists && (employeesSourceCount === 0 || employeesPreparedCount >= employeesSourceCount);
  const calendarReady = nextYearExists && monthsPreparedCount > 0;
  const canFinalize = nextYearExists && classesReady && personnelReady && calendarReady && recouvrementReviewed && finalConfirm;

  const steps = useMemo(() => ([
    {
      id: 'annee',
      title: 'Nouvelle annee',
      subtitle: 'Ouvrir le dossier',
      icon: Calendar,
      done: nextYearExists
    },
    {
      id: 'classes',
      title: 'Classes',
      subtitle: 'Preparer la structure',
      icon: BookOpen,
      done: classesReady
    },
    {
      id: 'personnel',
      title: 'Personnel',
      subtitle: 'Choisir qui reste',
      icon: Users,
      done: personnelReady
    },
    {
      id: 'calendrier',
      title: 'Calendrier',
      subtitle: 'Regler les mois',
      icon: FileText,
      done: calendarReady
    },
    ...(recouvrementOuvert ? [{
      id: 'recouvrement',
      title: 'Recouvrement',
      subtitle: 'Voir les dettes',
      icon: Banknote,
      done: recouvrementReviewed
    }] : []),
    {
      id: 'validation',
      title: 'Cloture',
      subtitle: 'Verifier et terminer',
      icon: Shield,
      done: false
    }
  ]), [calendarReady, classesReady, nextYearExists, personnelReady, recouvrementOuvert, recouvrementReviewed]);

  const currentStepIndex = steps.findIndex((step) => step.id === activeStep);

  const showMessage = (type, text) => {
    setMessage({ type, text });
  };

  const loadPersonnel = async () => {
    if (!nextYearExists) return;
    setPersonnelLoading(true);
    try {
      const response = await api.anneesScolaires.getPersonnelReconduction();
      if (!response?.success) {
        throw new Error(response?.message || 'Impossible de charger le personnel');
      }
      setPersonnelData(response.data);
    } catch (err) {
      showMessage('error', err.message || 'Erreur lors du chargement du personnel');
    } finally {
      setPersonnelLoading(false);
    }
  };

  const loadCalendar = async () => {
    if (!nextYearExists) return;
    setCalendarLoading(true);
    try {
      const response = await api.anneesScolaires.getCalendrierCloture();
      if (!response?.success) {
        throw new Error(response?.message || 'Impossible de charger le calendrier');
      }
      setCalendarData(response.data);
      setCalendarMonths(response.data?.mois || []);
    } catch (err) {
      showMessage('error', err.message || 'Erreur lors du chargement du calendrier');
    } finally {
      setCalendarLoading(false);
    }
  };

  const loadRecouvrement = async () => {
    setRecouvrementLoading(true);
    try {
      const response = await api.anneesScolaires.getRecouvrementCloture();
      if (!response?.success) {
        throw new Error(response?.message || 'Impossible de charger le recouvrement');
      }
      setRecouvrementData(response.data);
      setRecouvrementConfirm(Number(response.data?.synthese?.reste_a_recouvrer || 0) <= 0);
    } catch (err) {
      showMessage('error', err.message || 'Erreur lors du chargement du recouvrement');
    } finally {
      setRecouvrementLoading(false);
    }
  };

  useEffect(() => {
    if (activeStep === 'personnel' && nextYearExists) {
      loadPersonnel();
    }
    if (activeStep === 'calendrier' && nextYearExists) {
      loadCalendar();
    }
    if (activeStep === 'recouvrement' && recouvrementOuvert) {
      loadRecouvrement();
    }
    if (activeStep === 'validation' && nextYearExists) {
      loadPersonnel();
      if (recouvrementOuvert && !recouvrementData) {
        loadRecouvrement();
      }
    }
  }, [activeStep, nextYearExists, recouvrementOuvert]);

  const goNext = () => {
    const nextStep = steps[currentStepIndex + 1];
    if (nextStep) setActiveStep(nextStep.id);
  };

  const goPrevious = () => {
    const previousStep = steps[currentStepIndex - 1];
    if (previousStep) setActiveStep(previousStep.id);
  };

  const handleCreateNextYear = async () => {
    setCreatingNextYear(true);
    setMessage(null);
    try {
      const response = await api.anneesScolaires.creerAutomatiquementSuivante();
      if (!response?.success) {
        throw new Error(response?.message || 'Impossible de creer la nouvelle annee');
      }
      showMessage('success', response.message || 'Nouvelle annee ouverte.');
      await loadDiagnostic({ silent: true });
      setActiveStep('classes');
    } catch (err) {
      showMessage('error', err.message || 'Erreur lors de la creation');
    } finally {
      setCreatingNextYear(false);
    }
  };

  const handlePrepareClasses = async () => {
    setPreparingPedagogy(true);
    setMessage(null);
    try {
      const response = await api.anneesScolaires.preparerStructurePedagogique();
      if (!response?.success) {
        throw new Error(response?.message || 'Impossible de preparer les classes');
      }
      showMessage('success', response.message || 'Classes preparees.');
      await loadDiagnostic({ silent: true });
      setActiveStep('personnel');
    } catch (err) {
      showMessage('error', err.message || 'Erreur lors de la preparation des classes');
    } finally {
      setPreparingPedagogy(false);
    }
  };

  const handlePreparePersonnel = async () => {
    setPreparingPersonnel(true);
    setMessage(null);
    try {
      const response = await api.anneesScolaires.preparerPersonnel();
      if (!response?.success) {
        throw new Error(response?.message || 'Impossible de preparer la liste du personnel');
      }
      showMessage('success', response.message || 'Liste du personnel preparee.');
      await loadDiagnostic({ silent: true });
      await loadPersonnel();
    } catch (err) {
      showMessage('error', err.message || 'Erreur lors de la preparation du personnel');
    } finally {
      setPreparingPersonnel(false);
    }
  };

  const updatePersonnelDecision = (id, decision) => {
    setPersonnelData((previous) => ({
      ...previous,
      personnel: (previous?.personnel || []).map((employee) => (
        employee.id === id ? { ...employee, decision } : employee
      ))
    }));
  };

  const handleSavePersonnel = async () => {
    setPersonnelSaving(true);
    setMessage(null);
    try {
      const decisions = (personnelData?.personnel || []).map((employee) => ({
        id: employee.id,
        decision: employee.decision
      }));
      const response = await api.anneesScolaires.modifierDecisionsPersonnel(decisions);
      if (!response?.success) {
        throw new Error(response?.message || 'Impossible d enregistrer les choix');
      }
      setPersonnelData(response.data);
      showMessage('success', 'Choix du personnel enregistres.');
      await loadDiagnostic({ silent: true });
      await loadPersonnel();
    } catch (err) {
      showMessage('error', err.message || 'Erreur lors de l enregistrement');
    } finally {
      setPersonnelSaving(false);
    }
  };

  const updateMonth = (index, field, value) => {
    setCalendarMonths((previous) => previous.map((month, monthIndex) => (
      monthIndex === index ? { ...month, [field]: value } : month
    )));
  };

  const addCalendarMonth = () => {
    setCalendarMonths((previous) => [
      ...previous,
      buildNextCalendarMonth(previous, calendarData?.annee_suivante?.nom || nextYearName)
    ]);
  };

  const handleSaveCalendar = async () => {
    setCalendarSaving(true);
    setMessage(null);
    try {
      const response = await api.anneesScolaires.sauvegarderCalendrierCloture(calendarMonths);
      if (!response?.success) {
        throw new Error(response?.message || 'Impossible d enregistrer le calendrier');
      }
      setCalendarData(response.data);
      setCalendarMonths(response.data?.mois || []);
      showMessage('success', 'Calendrier scolaire enregistre.');
      await loadDiagnostic({ silent: true });
    } catch (err) {
      showMessage('error', err.message || 'Erreur lors de l enregistrement du calendrier');
    } finally {
      setCalendarSaving(false);
    }
  };

  const handleFinalize = async () => {
    setShowFinalizeConfirm(true);
  };

  const executeFinalize = async () => {
    setShowFinalizeConfirm(false);
    setShowYearTransition(true);
    setFinalizing(true);
    setMessage(null);
    try {
      const response = await api.anneesScolaires.finaliserCloture();
      if (!response?.success) {
        throw new Error(response?.message || 'Impossible de cloturer l annee');
      }

      await new Promise((resolve) => setTimeout(resolve, 1400));

      try {
        await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
      } catch (logoutError) {
        console.warn('Erreur logout apres cloture:', logoutError);
      }

      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('isLoggedInNew');
      localStorage.removeItem('permissions');
      window.location.href = '/connexion';
    } catch (err) {
      setShowYearTransition(false);
      showMessage('error', err.message || 'Erreur lors de la cloture');
      setFinalizing(false);
    }
  };

  const filteredPersonnel = (personnelData?.personnel || []).filter((employee) => {
    const search = personnelSearch.trim().toLowerCase();
    if (!search) return true;
    return [
      employee.nom,
      employee.prenom,
      employee.email,
      employee.role_principal_nom,
      employee.groupe_securite_nom
    ].some((value) => String(value || '').toLowerCase().includes(search));
  });

  const personnelSummary = useMemo(() => {
    const rows = personnelData?.personnel || [];
    return rows.reduce((acc, employee) => ({
      ...acc,
      [employee.decision]: (acc[employee.decision] || 0) + 1,
      total: acc.total + 1
    }), { total: 0, reconduit: 0, a_revoir: 0, non_reconduit: 0 });
  }, [personnelData]);

  const hasPersonnelRows = personnelSummary.total > 0;
  const personnelFinalSummary = {
    total: hasPersonnelRows
      ? personnelSummary.total
      : Number(rhDiagnosticSummary.total || employeesPreparedCount || employeesSourceCount || 0),
    reconduit: hasPersonnelRows
      ? personnelSummary.reconduit
      : Number(rhDiagnosticSummary.reconduits || 0),
    aRevoir: hasPersonnelRows
      ? personnelSummary.a_revoir
      : Number(rhDiagnosticSummary.a_revoir || 0),
    nonReconduit: hasPersonnelRows
      ? personnelSummary.non_reconduit
      : Number(rhDiagnosticSummary.non_reconduits || 0)
  };
  const personnelCarriedCount = personnelFinalSummary.reconduit + personnelFinalSummary.aRevoir;
  const tenantSnapshot = getTenantSnapshot();

  if (loading) {
    return (
      <div className="w-full p-8">
        <div className="flex min-h-[360px] flex-col items-center justify-center rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="mb-4 h-14 w-14 rounded-full border-4 border-[#246990] border-t-transparent animate-spin" />
          <h2 className="text-xl font-bold text-[#246990]">Preparation de la cloture annuelle</h2>
          <p className="mt-2 text-sm text-gray-500">Chargement du parcours...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-8">
        <AlertBox type="danger" title="Cloture annuelle indisponible">
          <p>{error}</p>
          <div className="mt-4">
            <ActionButton icon={RefreshCw} tone="light" onClick={() => loadDiagnostic()}>
              Reessayer
            </ActionButton>
          </div>
        </AlertBox>
      </div>
    );
  }

  const renderNavigation = (nextDisabled = false, nextLabel = 'Suivant') => (
    <>
      <ActionButton icon={ChevronLeft} tone="light" disabled={currentStepIndex === 0} onClick={goPrevious}>
        Precedent
      </ActionButton>
      <ActionButton icon={ChevronRight} disabled={nextDisabled || currentStepIndex === steps.length - 1} onClick={goNext}>
        {nextLabel}
      </ActionButton>
    </>
  );

  const renderStep = () => {
    if (activeStep === 'annee') {
      return (
        <StepLayout
          icon={Calendar}
          title="Ouvrir la nouvelle annee"
          subtitle="On cree simplement le dossier qui recevra les classes, le personnel et les reinscriptions."
          footer={nextYearExists ? renderNavigation(false) : (
            <>
              <ActionButton icon={ChevronLeft} tone="light" disabled>Precedent</ActionButton>
              <ActionButton icon={Calendar} loading={creatingNextYear} onClick={handleCreateNextYear}>
                Ouvrir {nextYearName}
              </ActionButton>
            </>
          )}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <InfoLine label="Annee actuelle" value={currentYearName} tone="blue" />
            <InfoLine label="Nouvelle annee" value={nextYearName} tone={nextYearExists ? 'teal' : 'amber'} />
            <InfoLine label="Etat" value={nextYearExists ? 'Ouverte' : 'A ouvrir'} tone={nextYearExists ? 'teal' : 'amber'} />
          </div>

          <div className="mt-6 max-w-3xl">
            <AlertBox type={nextYearExists ? 'success' : 'info'} title={nextYearExists ? 'La nouvelle annee existe deja' : 'Pourquoi cette etape ?'}>
              {nextYearExists
                ? `Le dossier ${nextYearName} est pret. Vous pouvez maintenant preparer les classes.`
                : `Avant toute cloture, Classis doit creer ${nextYearName}. Cela ne copie aucun eleve et ne ferme pas encore ${currentYearName}.`}
            </AlertBox>
          </div>
        </StepLayout>
      );
    }

    if (activeStep === 'classes') {
      return (
        <StepLayout
          icon={BookOpen}
          title="Preparer les classes"
          subtitle="Les eleves ne sont pas copies. On prepare seulement les destinations de reinscription."
          footer={classesReady ? renderNavigation(false) : (
            <>
              <ActionButton icon={ChevronLeft} tone="light" onClick={goPrevious}>Precedent</ActionButton>
              <ActionButton icon={BookOpen} loading={preparingPedagogy} disabled={!nextYearExists} onClick={handlePrepareClasses}>
                Preparer les classes
              </ActionButton>
            </>
          )}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <InfoLine label="Classes actuelles" value={formatNumber(classesSourceCount)} tone="blue" />
            <InfoLine label="Deja preparees" value={formatNumber(classesPreparedCount)} tone={classesReady ? 'teal' : 'amber'} />
            <InfoLine label="Matieres/classes" value={formatNumber(indicateurs.associations_matieres_classes_prochaine_annee)} tone="slate" />
          </div>

          <div className="mt-6 max-w-3xl space-y-3">
            {!nextYearExists && (
              <AlertBox type="warning" title="Nouvelle annee requise">
                Ouvrez d abord {nextYearName}, puis revenez preparer les classes.
              </AlertBox>
            )}
            {classesReady ? (
              <AlertBox type="success" title="Classes preparees">
                Les classes de {nextYearName} sont pretes pour la reinscription des eleves.
              </AlertBox>
            ) : (
              <AlertBox type="info" title="Ce qui sera repris">
                Les sections, classes, frais de classe et liens matieres/classes seront prepares. Les eleves et les paiements resteront dans {currentYearName}.
              </AlertBox>
            )}
          </div>
        </StepLayout>
      );
    }

    if (activeStep === 'personnel') {
      const hasPersonnel = (personnelData?.personnel || []).length > 0;

      return (
        <StepLayout
          icon={Users}
          title="Choisir le personnel reconduit"
          subtitle="Cochez les personnes qui doivent continuer dans la nouvelle annee. Les autres restent hors reconduction."
          footer={
            <>
              <ActionButton icon={ChevronLeft} tone="light" onClick={goPrevious}>Precedent</ActionButton>
              <div className="flex flex-wrap gap-3">
                {!hasPersonnel && (
                  <ActionButton icon={Users} loading={preparingPersonnel} disabled={!nextYearExists} onClick={handlePreparePersonnel}>
                    Generer la liste du personnel
                  </ActionButton>
                )}
                {hasPersonnel && (
                  <ActionButton icon={CheckCircle} loading={personnelSaving} onClick={handleSavePersonnel}>
                    Enregistrer les choix
                  </ActionButton>
                )}
                <ActionButton icon={ChevronRight} disabled={!personnelReady} onClick={goNext}>
                  Suivant
                </ActionButton>
              </div>
            </>
          }
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <InfoLine label="Total" value={formatNumber(personnelSummary.total || employeesSourceCount)} tone="blue" />
            <InfoLine label="Reconduits" value={formatNumber(personnelSummary.reconduit)} tone="teal" />
            <InfoLine label="A revoir" value={formatNumber(personnelSummary.a_revoir)} tone="amber" />
            <InfoLine label="Non reconduits" value={formatNumber(personnelSummary.non_reconduit)} tone="red" />
          </div>

          {!nextYearExists && (
            <div className="mt-6">
              <AlertBox type="warning" title="Nouvelle annee requise">
                Ouvrez d abord {nextYearName} pour pouvoir preparer la liste du personnel.
              </AlertBox>
            </div>
          )}

          {nextYearExists && !hasPersonnel && !personnelLoading && (
            <div className="mt-6 max-w-3xl">
              <AlertBox type="info" title="La liste n est pas encore generee">
                Cliquez sur le bouton de generation. Classis va proposer le personnel actif, puis vous pourrez decocher les personnes a ne pas reconduire.
              </AlertBox>
            </div>
          )}

          {personnelLoading && (
            <div className="mt-6 flex items-center gap-2 text-sm font-semibold text-[#246990]">
              <RefreshCw size={16} className="animate-spin" />
              Chargement du personnel...
            </div>
          )}

          {hasPersonnel && (
            <div className="mt-6">
              <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="relative w-full md:max-w-md">
                  <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={17} />
                  <input
                    type="text"
                    value={personnelSearch}
                    onChange={(event) => setPersonnelSearch(event.target.value)}
                    placeholder="Rechercher un employe..."
                    className="h-11 w-full rounded-lg border border-gray-200 bg-white pl-10 pr-3 text-sm outline-none focus:border-[#246990]"
                  />
                </div>
                <Badge className="border-[#246990]/20 bg-[#246990]/10 text-[#246990]">
                  {formatNumber(filteredPersonnel.length)} affiche(s)
                </Badge>
              </div>

              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-100 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="w-28 px-4 py-3 text-left font-bold text-gray-600">Reconduire</th>
                      <th className="px-4 py-3 text-left font-bold text-gray-600">Personnel</th>
                      <th className="px-4 py-3 text-left font-bold text-gray-600">Role</th>
                      <th className="px-4 py-3 text-left font-bold text-gray-600">Decision</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {filteredPersonnel.map((employee) => (
                      <tr key={employee.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={employee.decision !== 'non_reconduit'}
                            onChange={(event) => updatePersonnelDecision(employee.id, event.target.checked ? 'reconduit' : 'non_reconduit')}
                            className="h-5 w-5 rounded border-gray-300 text-[#246990] focus:ring-[#246990]"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-bold text-gray-900">{employee.prenom} {employee.nom}</p>
                          <p className="text-xs text-gray-500">{employee.email || 'Email non renseigne'}</p>
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {employee.role_principal_nom || employee.groupe_securite_nom || 'Role non renseigne'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-2">
                            {['reconduit', 'a_revoir', 'non_reconduit'].map((decision) => (
                              <button
                                key={decision}
                                type="button"
                                onClick={() => updatePersonnelDecision(employee.id, decision)}
                                className={`rounded-md border px-2.5 py-1 text-xs font-semibold ${
                                  employee.decision === decision
                                    ? decisionClasses[decision]
                                    : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'
                                }`}
                              >
                                {decisionLabels[decision]}
                              </button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </StepLayout>
      );
    }

    if (activeStep === 'calendrier') {
      return (
        <StepLayout
          icon={FileText}
          title="Regler le calendrier scolaire"
          subtitle="Verifiez les mois de la nouvelle annee avant de les enregistrer."
          footer={
            <>
              <ActionButton icon={ChevronLeft} tone="light" onClick={goPrevious}>Precedent</ActionButton>
              <div className="flex flex-wrap gap-3">
                <ActionButton icon={FileText} loading={calendarSaving} disabled={!nextYearExists || calendarMonths.length === 0} onClick={handleSaveCalendar}>
                  Enregistrer le calendrier
                </ActionButton>
                <ActionButton icon={ChevronRight} disabled={!calendarReady} onClick={goNext}>
                  Suivant
                </ActionButton>
              </div>
            </>
          }
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <InfoLine label="Modele actuel" value={formatNumber(monthsSourceCount || calendarData?.resume?.source)} tone="blue" />
            <InfoLine label="Nouvelle annee" value={formatNumber(calendarMonths.length)} tone={calendarReady ? 'teal' : 'amber'} />
            <InfoLine label="Paiements copies" value="0" tone="slate" />
          </div>

          {calendarLoading && (
            <div className="mt-6 flex items-center gap-2 text-sm font-semibold text-[#246990]">
              <RefreshCw size={16} className="animate-spin" />
              Chargement du calendrier...
            </div>
          )}

          {!calendarLoading && calendarMonths.length > 0 && (
            <div className="mt-6">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <p className="font-bold text-gray-900">Mois de la nouvelle annee</p>
                  <p className="text-sm text-gray-500">Ajoutez juillet ou aout si l etablissement prolonge son exercice.</p>
                </div>
                <ActionButton icon={Plus} tone="light" onClick={addCalendarMonth}>
                  Ajouter un mois
                </ActionButton>
              </div>

              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-100 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-bold text-gray-600">Mois</th>
                      <th className="px-4 py-3 text-left font-bold text-gray-600">Trimestre</th>
                      <th className="px-4 py-3 text-left font-bold text-gray-600">Debut</th>
                      <th className="px-4 py-3 text-left font-bold text-gray-600">Fin</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {calendarMonths.map((month, index) => (
                      <tr key={`${month.ordre}-${month.nom}`}>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={month.nom || ''}
                            onChange={(event) => updateMonth(index, 'nom', event.target.value)}
                            className="h-10 w-full rounded-lg border border-gray-200 px-3 outline-none focus:border-[#246990]"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="1"
                            max="3"
                            value={month.trimestre || 1}
                            onChange={(event) => updateMonth(index, 'trimestre', event.target.value)}
                            className="h-10 w-24 rounded-lg border border-gray-200 px-3 outline-none focus:border-[#246990]"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="date"
                            value={month.date_debut || ''}
                            onChange={(event) => updateMonth(index, 'date_debut', event.target.value)}
                            className="h-10 rounded-lg border border-gray-200 px-3 outline-none focus:border-[#246990]"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="date"
                            value={month.date_fin || ''}
                            onChange={(event) => updateMonth(index, 'date_fin', event.target.value)}
                            className="h-10 rounded-lg border border-gray-200 px-3 outline-none focus:border-[#246990]"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="mt-6 max-w-3xl">
            <AlertBox type="info" title="Important">
              Les mois scolaires sont prepares, mais les paiements ne sont pas copies. Les lignes de paiement seront creees pendant la reinscription.
            </AlertBox>
          </div>
        </StepLayout>
      );
    }

    if (activeStep === 'recouvrement' && recouvrementOuvert) {
      const repartitionClasses = recouvrementData?.repartition_par_classe || [];
      const repartitionMois = recouvrementData?.repartition_mois_payes || [];
      const elevesDebiteurs = recouvrementData?.eleves || [];
      const maxDistribution = Math.max(...repartitionMois.map((item) => item.eleves || 0), 1);

      return (
        <StepLayout
          icon={Banknote}
          title="Verifier le recouvrement"
          subtitle={`On mesure ce qui reste du sur ${currentYearName}. La cloture scolaire reste possible, mais les dettes seront suivies apres ouverture de ${nextYearName}.`}
          footer={
            <>
              <ActionButton icon={ChevronLeft} tone="light" onClick={goPrevious}>Precedent</ActionButton>
              <div className="flex flex-wrap gap-3">
                <ActionButton icon={RefreshCw} tone="light" loading={recouvrementLoading} onClick={loadRecouvrement}>
                  Actualiser
                </ActionButton>
                <ActionButton icon={ChevronRight} disabled={!recouvrementReviewed} onClick={goNext}>
                  Suivant
                </ActionButton>
              </div>
            </>
          }
        >
          {recouvrementLoading && !recouvrementData && (
            <div className="flex items-center gap-2 text-sm font-semibold text-[#246990]">
              <RefreshCw size={16} className="animate-spin" />
              Analyse des paiements de fin d annee...
            </div>
          )}

          {recouvrementData && (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <InfoLine label="Attendu annuel" value={formatMoney(recouvrementSummary.montant_attendu)} tone="blue" />
                <InfoLine label="Deja encaisse" value={formatMoney(recouvrementSummary.montant_paye)} tone="teal" />
                <InfoLine label="Reste a recouvrer" value={formatMoney(resteARecouvrer)} tone={resteARecouvrer > 0 ? 'red' : 'teal'} />
                <InfoLine label="Eleves concernes" value={formatNumber(recouvrementSummary.eleves_concernes)} tone={resteARecouvrer > 0 ? 'amber' : 'teal'} />
              </div>

              <div className="mt-6 grid grid-cols-1 gap-5 xl:grid-cols-[1.1fr_0.9fr]">
                <div className="rounded-lg border border-gray-200 bg-white">
                  <div className="border-b border-gray-100 px-5 py-4">
                    <h3 className="font-bold text-gray-900">Repartition par section et classe</h3>
                    <p className="mt-1 text-sm text-gray-500">Les classes qui portent encore des frais scolaires non soldes.</p>
                  </div>
                  <div className="max-h-[320px] overflow-auto">
                    {repartitionClasses.length === 0 ? (
                      <div className="px-5 py-8 text-center text-sm text-gray-500">Aucune dette par classe.</div>
                    ) : (
                      <table className="min-w-full divide-y divide-gray-100 text-sm">
                        <thead className="sticky top-0 bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left font-bold text-gray-600">Section</th>
                            <th className="px-4 py-3 text-left font-bold text-gray-600">Classe</th>
                            <th className="px-4 py-3 text-right font-bold text-gray-600">Eleves</th>
                            <th className="px-4 py-3 text-right font-bold text-gray-600">Reste</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {repartitionClasses.map((row) => (
                            <tr key={`${row.section_id}-${row.classe_id}`}>
                              <td className="px-4 py-3 text-gray-600">{row.section_nom}</td>
                              <td className="px-4 py-3 font-semibold text-gray-900">{row.classe_nom}</td>
                              <td className="px-4 py-3 text-right text-gray-700">{formatNumber(row.eleves_concernes)}</td>
                              <td className="px-4 py-3 text-right font-bold text-[#c66d59]">{formatMoney(row.reste_a_recouvrer)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white">
                  <div className="border-b border-gray-100 px-5 py-4">
                    <h3 className="font-bold text-gray-900">Mois payes par eleve</h3>
                    <p className="mt-1 text-sm text-gray-500">Lecture rapide du niveau de paiement sur l annee.</p>
                  </div>
                  <div className="space-y-3 px-5 py-5">
                    {repartitionMois.length === 0 ? (
                      <p className="text-sm text-gray-500">Aucune ligne de paiement a analyser.</p>
                    ) : repartitionMois.map((row) => {
                      const width = `${Math.max(8, Math.round((row.eleves / maxDistribution) * 100))}%`;
                      return (
                        <div key={`${row.mois_payes}-${row.mois_total}`}>
                          <div className="mb-1 flex items-center justify-between text-sm">
                            <span className="font-semibold text-gray-700">{row.mois_payes}/{row.mois_total} mois payes</span>
                            <span className="font-bold text-[#246990]">{formatNumber(row.eleves)} eleve(s)</span>
                          </div>
                          <div className="h-2.5 rounded-full bg-gray-100">
                            <div className="h-2.5 rounded-full bg-[#319190]" style={{ width }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="mt-6 rounded-lg border border-gray-200 bg-white">
                <div className="flex flex-col gap-2 border-b border-gray-100 px-5 py-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h3 className="font-bold text-gray-900">Eleves a recouvrer</h3>
                    <p className="mt-1 text-sm text-gray-500">Apercu des plus gros restes a payer. Le module Recouvrement reprendra cette liste apres cloture.</p>
                  </div>
                  <Badge className="border-[#c66d59]/20 bg-[#c66d59]/10 text-[#c66d59]">
                    {formatNumber(elevesDebiteurs.length)} dossier(s)
                  </Badge>
                </div>
                <div className="max-h-[360px] overflow-auto">
                  {elevesDebiteurs.length === 0 ? (
                    <div className="px-5 py-8 text-center text-sm text-gray-500">Aucun eleve debiteur sur cette annee.</div>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-100 text-sm">
                      <thead className="sticky top-0 bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left font-bold text-gray-600">Eleve</th>
                          <th className="px-4 py-3 text-left font-bold text-gray-600">Classe</th>
                          <th className="px-4 py-3 text-left font-bold text-gray-600">Parent</th>
                          <th className="px-4 py-3 text-left font-bold text-gray-600">Mois</th>
                          <th className="px-4 py-3 text-right font-bold text-gray-600">Reste</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {elevesDebiteurs.slice(0, 12).map((eleve) => (
                          <tr key={eleve.eleve_id}>
                            <td className="px-4 py-3">
                              <p className="font-bold text-gray-900">{eleve.prenom} {eleve.nom}</p>
                              <p className="text-xs text-gray-500">{eleve.matricule || 'Matricule non renseigne'}</p>
                            </td>
                            <td className="px-4 py-3 text-gray-700">{eleve.classe_nom}</td>
                            <td className="px-4 py-3">
                              <p className="font-semibold text-gray-800">{eleve.parent_nom}</p>
                              <p className="text-xs text-gray-500">{eleve.parent_telephone || 'Telephone non renseigne'}</p>
                            </td>
                            <td className="px-4 py-3 text-gray-600">
                              {(eleve.mois_impayes || []).slice(0, 3).join(', ')}
                              {(eleve.mois_impayes || []).length > 3 ? '...' : ''}
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-[#c66d59]">{formatMoney(eleve.reste_a_recouvrer)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              <div className={`mt-6 rounded-lg border p-4 ${resteARecouvrer > 0 ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
                {resteARecouvrer > 0 ? (
                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={recouvrementConfirm}
                      onChange={(event) => setRecouvrementConfirm(event.target.checked)}
                      className="mt-1 h-5 w-5 rounded border-amber-300 text-[#246990] focus:ring-[#246990]"
                    />
                    <span>
                      <span className="flex items-center gap-2 font-bold text-amber-900">
                        <AlertCircle size={17} />
                        Je confirme que {formatMoney(resteARecouvrer)} restent a recouvrer sur {currentYearName}
                      </span>
                      <span className="mt-1 block text-sm text-amber-800">
                        La cloture scolaire pourra continuer. Ces dettes resteront rattachees a {currentYearName} et seront traitees depuis le module Recouvrement de la nouvelle annee active.
                      </span>
                    </span>
                  </label>
                ) : (
                  <div className="flex items-start gap-3 text-emerald-800">
                    <CheckCircle size={19} />
                    <div>
                      <p className="font-bold">Aucun reste a recouvrer</p>
                      <p className="mt-1 text-sm">L exercice scolaire ne presente aucune dette de frais scolaires dans les lignes analysees.</p>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </StepLayout>
      );
    }

    return (
      <StepLayout
        icon={Shield}
        title="Verifier et cloturer"
        subtitle="Cette action ferme l annee actuelle, active la nouvelle annee et force une reconnexion."
        footer={
          <>
            <ActionButton icon={ChevronLeft} tone="light" onClick={goPrevious}>Precedent</ActionButton>
            <ActionButton icon={LogOut} tone="danger" loading={finalizing} disabled={!canFinalize} onClick={handleFinalize}>
              Cloturer et deconnecter
            </ActionButton>
          </>
        }
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <AlertBox type={nextYearExists ? 'success' : 'warning'} title="Nouvelle annee">
            {nextYearExists ? `${nextYearName} est ouverte.` : 'La nouvelle annee doit etre ouverte.'}
          </AlertBox>
          <AlertBox type={classesReady ? 'success' : 'warning'} title="Classes">
            {classesReady ? `${formatNumber(classesPreparedCount)} classe(s) preparee(s).` : 'Les classes doivent etre preparees.'}
          </AlertBox>
          <AlertBox type={personnelReady ? 'success' : 'warning'} title="Personnel">
            {personnelReady
              ? `${formatNumber(personnelCarriedCount)} personne(s) reconduite(s) vers ${nextYearName}. ${formatNumber(personnelFinalSummary.nonReconduit)} non reconduite(s).`
              : 'Le personnel doit etre choisi.'}
          </AlertBox>
          <AlertBox type={calendarReady ? 'success' : 'warning'} title="Calendrier">
            {calendarReady ? `${formatNumber(monthsPreparedCount)} mois scolaire(s) pret(s).` : 'Le calendrier doit etre enregistre.'}
          </AlertBox>
          {recouvrementOuvert && (
            <AlertBox type={recouvrementReviewed ? 'success' : 'warning'} title="Recouvrement">
              {recouvrementReviewed
                ? resteARecouvrer > 0
                  ? `${formatMoney(resteARecouvrer)} seront suivis apres cloture.`
                  : 'Aucun reste a recouvrer.'
                : 'Le recouvrement doit etre consulte et confirme.'}
            </AlertBox>
          )}
        </div>

        <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4">
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={finalConfirm}
              onChange={(event) => setFinalConfirm(event.target.checked)}
              className="mt-1 h-5 w-5 rounded border-red-300 text-red-600 focus:ring-red-600"
            />
            <span>
              <span className="block font-bold text-red-800">Je confirme la cloture de {currentYearName}</span>
              <span className="mt-1 block text-sm text-red-700">
                Apres validation, {currentYearName} sera fermee, {nextYearName} deviendra l annee active, puis Classis me ramenera a la page de connexion.
              </span>
            </span>
          </label>
        </div>
      </StepLayout>
    );
  };

  return (
    <>
      {showYearTransition && (
        <YearTransitionOverlay
          currentYearName={currentYearName}
          nextYearName={nextYearName}
          tenantLabel={`${tenantSnapshot.tenant} - ${tenantSnapshot.schoolName}`}
        />
      )}

      <ConfirmDialog
        isOpen={showFinalizeConfirm}
        onClose={() => setShowFinalizeConfirm(false)}
        onConfirm={executeFinalize}
        title="Confirmer la cloture annuelle"
        description={`Vous etes sur le point de fermer ${currentYearName} et d activer ${nextYearName}.`}
        confirmText="Oui, cloturer"
        cancelText="Annuler"
        type="warning"
        customIcon={<Building2 size={32} />}
        isLoading={finalizing}
        confirmDisabled={!canFinalize}
        contentClassName="sm:max-w-2xl"
      >
        <div className="space-y-4">
          <div className="rounded-lg border border-[#246990]/15 bg-[#246990]/5 p-4">
            <p className="text-xs font-bold uppercase tracking-wide text-[#246990]">Tenant concerne</p>
            <p className="mt-1 text-lg font-bold text-gray-900">{tenantSnapshot.tenant}</p>
            <p className="text-sm text-gray-600">{tenantSnapshot.schoolName}</p>
          </div>

          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 rounded-lg border border-gray-200 bg-white p-4">
            <div>
              <p className="text-xs font-bold uppercase text-gray-500">Annee actuelle</p>
              <p className="mt-1 text-lg font-bold text-gray-900">{currentYearName}</p>
            </div>
            <ArrowRight className="text-[#246990]" size={22} />
            <div>
              <p className="text-xs font-bold uppercase text-[#319190]">Nouvelle annee</p>
              <p className="mt-1 text-lg font-bold text-gray-900">{nextYearName}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700">
              {formatNumber(classesPreparedCount)} classe(s) preparee(s)
            </Badge>
            <Badge className="border-[#319190]/20 bg-[#319190]/10 text-[#319190]">
              {formatNumber(personnelCarriedCount)} personnel(s) reconduit(s)
            </Badge>
            <Badge className="border-red-200 bg-red-50 text-red-700">
              {formatNumber(personnelFinalSummary.nonReconduit)} personnel(s) non reconduit(s)
            </Badge>
            <Badge className="border-amber-200 bg-amber-50 text-amber-700">
              {formatNumber(calendarMonths.length || monthsPreparedCount)} mois scolaire(s)
            </Badge>
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            La cloture va modifier l annee active du tenant. Apres confirmation, Classis vous deconnectera pour revenir proprement sur la page de connexion.
          </div>
        </div>
      </ConfirmDialog>

      <div className="w-full bg-transparent p-8">
      <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-[#246990]">Outils administratifs</p>
          <h1 className="mt-1 text-3xl font-bold text-[#246990]">Cloture annuelle</h1>
          <p className="mt-2 max-w-3xl text-sm text-gray-600">
            Assistant de preparation de la nouvelle annee scolaire.
          </p>
        </div>
        <ActionButton icon={RefreshCw} tone="light" loading={refreshing} onClick={() => loadDiagnostic({ silent: true })}>
          Actualiser
        </ActionButton>
      </div>

      <Stepper activeStep={activeStep} setActiveStep={setActiveStep} steps={steps} />

      {message && (
        <div className="mt-6">
          <AlertBox type={message.type === 'error' ? 'danger' : message.type === 'success' ? 'success' : 'info'}>
            {message.text}
          </AlertBox>
        </div>
      )}

      <div className="mt-6">
        {renderStep()}
      </div>
      </div>
    </>
  );
};

export default ClotureAnnee;
